/**
 * Disponibilidade — quando cada professor pode dar aula.
 *
 * Antes, esta entrada do menu abria a tabela crua de `preferencia`: uma
 * linha por janela, com o código do professor, o dia como número e o horário
 * em minutos desde a meia-noite ("450" para 07:30). Eram 7.533 linhas na
 * ESPM, e ninguém pensa disponibilidade assim.
 *
 * Agora: escolhe-se o professor e aparece a semana dele em quadradinhos — a
 * mesma que ele vê no formulário por link. É aqui que a coordenação ajusta
 * quando o professor manda "mudou, agora posso na quinta", que é o que hoje
 * se faz à mão no Urânia (§23). Em cima, o que a carga dele pede, conferido
 * enquanto se marca.
 *
 * A lista técnica continua numa aba, para as regras que não são de professor
 * (turma, sala) e para o "prefere / prefere não".
 */

import { useCallback, useEffect, useMemo, useState } from 'react'

import { ErroApi, formularios, pedir, type FormularioDoProfessor } from '../api'
import { LegendaDaSemana, SemanaDeDisponibilidade } from '../components/SemanaDeDisponibilidade'
import { chaveDoTempo, cobre, contagem, type ExigenciaDoFormulario, type TempoDoFormulario } from '../formulario'
import { type Registro, rotulosDistintos } from '../rotulos'

interface Semana {
  professor: { id: string; nome: string; codigo_externo: string | null }
  turnos: { id: string; nome: string }[]
  tempos: TempoDoFormulario[]
  marcados: string[]
  pedido: { exigencias: ExigenciaDoFormulario[]; total: number } | null
  frases: string[]
  outras_preferencias: number
  negociacao: Negociacao | null
}

interface Negociacao {
  id: string
  estado: 'ABERTA' | 'EM_NEGOCIACAO' | 'AGUARDANDO' | 'RESOLVIDA' | 'DESCARTADA'
  responsavel: string | null
  ultima_nota: string | null
  notas: number
}

const ESTADO_DA_NEGOCIACAO: Record<Negociacao['estado'], string> = {
  ABERTA: 'aberta, sem responsável',
  EM_NEGOCIACAO: 'em andamento',
  AGUARDANDO: 'aguardando o professor',
  RESOLVIDA: 'concluída',
  DESCARTADA: 'descartada',
}

type Filtro = 'TODOS' | 'NAO_COBRE' | 'SEM_CARGA'

const semAcento = (s: string) =>
  s.normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase()

export function Disponibilidade({ projeto, listaCompleta }: { projeto: string; listaCompleta: React.ReactNode }) {
  const [aba, setAba] = useState<'SEMANA' | 'LISTA'>('SEMANA')
  const [lista, setLista] = useState<FormularioDoProfessor[]>([])
  const [busca, setBusca] = useState('')
  const [filtro, setFiltro] = useState<Filtro>('TODOS')
  const [escolhido, setEscolhido] = useState<string | null>(null)
  const [semana, setSemana] = useState<Semana | null>(null)
  const [marcados, setMarcados] = useState<Set<string>>(new Set())
  const [salvando, setSalvando] = useState(false)
  const [aviso, setAviso] = useState<{ tipo: 'ok' | 'erro'; texto: string } | null>(null)
  const [nota, setNota] = useState('')
  const [negociando, setNegociando] = useState(false)
  /**
   * A edição manual é travada por padrão. A disponibilidade é do professor;
   * a coordenação só altera o que combinou com ele. Para liberar: haver uma
   * negociação (aberta ou já finalizada), confirmar o acordo e escrever o que
   * foi combinado. O servidor confere as mesmas três coisas.
   */
  const [confirmando, setConfirmando] = useState(false)
  const [acordo, setAcordo] = useState(false)
  const [combinado, setCombinado] = useState('')
  const [editando, setEditando] = useState(false)

  const carregarLista = useCallback(async () => {
    try {
      setLista((await formularios.listar(projeto)).professores)
    } catch (e) {
      setAviso({ tipo: 'erro', texto: e instanceof ErroApi ? e.message : 'não foi possível carregar os professores' })
    }
  }, [projeto])

  useEffect(() => {
    void carregarLista()
  }, [carregarLista])

  const rotulos = useMemo(() => rotulosDistintos(lista as unknown as Registro[]), [lista])

  useEffect(() => {
    if (!escolhido) return
    setSemana(null)
    setAviso(null)
    setEditando(false)
    setConfirmando(false)
    setAcordo(false)
    setCombinado('')
    setNota('')
    pedir<Semana>(`/projetos/${projeto}/disponibilidade/${escolhido}`)
      .then((s) => {
        setSemana(s)
        setMarcados(new Set(s.marcados))
      })
      .catch((e) => setAviso({ tipo: 'erro', texto: e instanceof ErroApi ? e.message : 'não foi possível abrir' }))
  }, [projeto, escolhido])

  const alterado = useMemo(() => {
    if (!semana) return false
    const antes = new Set(semana.marcados)
    return antes.size !== marcados.size || [...marcados].some((k) => !antes.has(k))
  }, [semana, marcados])

  const escolher = (id: string) => {
    if (id === escolhido) return
    if (alterado && !window.confirm('Há mudanças não salvas nesta semana. Trocar de professor e descartá-las?')) return
    setEscolhido(id)
  }

  const visiveis = lista.filter((p) => {
    if (filtro === 'NAO_COBRE' && !(p.com_carga && p.faltas.length > 0)) return false
    if (filtro === 'SEM_CARGA' && p.com_carga) return false
    const alvo = semAcento(busca.trim())
    if (!alvo) return true
    return semAcento(`${rotulos.get(p.id) ?? p.nome} ${p.codigo_externo ?? ''}`).includes(alvo)
  })
  const quantos = (f: Filtro) =>
    lista.filter((p) =>
      f === 'NAO_COBRE' ? p.com_carga && p.faltas.length > 0 : f === 'SEM_CARGA' ? !p.com_carga : true,
    ).length

  const n = semana?.pedido ? contagem(semana.pedido.exigencias, semana.tempos, marcados) : []

  const mudar = (chaves: string[], pode: boolean) => {
    setMarcados((atual) => {
      const novo = new Set(atual)
      for (const k of chaves) {
        if (pode) novo.add(k)
        else novo.delete(k)
      }
      return novo
    })
    setAviso(null)
  }

  const salvar = async () => {
    if (!semana) return
    setSalvando(true)
    try {
      const r = await pedir<{ faltas: string[]; negociacao: Negociacao | null }>(`/projetos/${projeto}/disponibilidade/${semana.professor.id}`, {
        method: 'PUT',
        body: JSON.stringify({ marcados: [...marcados], acordo: true, nota: combinado }),
      })
      setSemana({ ...semana, marcados: [...marcados], negociacao: r.negociacao })
      setEditando(false)
      setAcordo(false)
      setCombinado('')
      setAviso({
        tipo: 'ok',
        texto:
          r.faltas.length === 0
            ? r.negociacao?.estado === 'RESOLVIDA' && semana.negociacao?.estado !== 'RESOLVIDA'
              ? 'Salvo, e agora cobre a carga — a negociação foi dada por resolvida.'
              : 'Salvo. A próxima vez que a grade for montada já usa esta disponibilidade.'
            : 'Salvo — mas ainda não cobre a carga. A grade vai deixar aula sem lugar até alguém resolver.',
      })
      void carregarLista()
    } catch (e) {
      setAviso({ tipo: 'erro', texto: e instanceof ErroApi ? e.message : 'não foi possível salvar' })
    } finally {
      setSalvando(false)
    }
  }

  const todos = semana ? semana.tempos.map(chaveDoTempo) : []
  // A negociação olha o que está SALVO, não o rascunho: negociar sobre uma
  // mudança que ninguém gravou seria negociar com o vento.
  const salvoCobre = semana ? cobre(semana.pedido, semana.tempos, new Set(semana.marcados)) : true
  // Negociação que libera a edição: aberta ou já finalizada. Descartada não.
  const negociacao = semana?.negociacao && semana.negociacao.estado !== 'DESCARTADA' ? semana.negociacao : null

  const cancelarEdicao = () => {
    if (semana) setMarcados(new Set(semana.marcados))
    setEditando(false)
    setConfirmando(false)
    setAcordo(false)
    setCombinado('')
  }

  const iniciarNegociacao = async () => {
    if (!semana) return
    setNegociando(true)
    try {
      const r = await pedir<{ negociacao: Negociacao }>(
        `/projetos/${projeto}/disponibilidade/${semana.professor.id}/negociacao`,
        { method: 'POST', body: JSON.stringify({ nota }) },
      )
      setSemana({ ...semana, negociacao: r.negociacao })
      setNota('')
      setAviso({
        tipo: 'ok',
        texto: 'Negociação iniciada — está em Pendências, com você como responsável. Quando combinar com o professor, use "Alterar disponibilidade".',
      })
    } catch (e) {
      setAviso({ tipo: 'erro', texto: e instanceof ErroApi ? e.message : 'não foi possível iniciar a negociação' })
    } finally {
      setNegociando(false)
    }
  }
  const situacao = (p: FormularioDoProfessor) =>
    !p.com_carga ? (
      <span className="disp__selo">sem carga</span>
    ) : p.faltas.length > 0 ? (
      <span className="disp__selo disp__selo--falta">não cobre</span>
    ) : (
      <span className="disp__selo disp__selo--ok">cobre</span>
    )

  return (
    <div className="section">
      <header className="page-head">
        <h1>Disponibilidade</h1>
        <p className="page-head__note">
          Quando cada professor pode dar aula — o que ele mesmo informou, no questionário ou no formulário por link.
          Só ele muda; a coordenação altera apenas o que foi combinado numa negociação.
        </p>
      </header>

      <div className="segmented segmented--texto" role="group" aria-label="Como ver">
        <button type="button" className="segmented__option" aria-pressed={aba === 'SEMANA'} onClick={() => setAba('SEMANA')}>
          Semana por professor
        </button>
        <button type="button" className="segmented__option" aria-pressed={aba === 'LISTA'} onClick={() => setAba('LISTA')}>
          Lista completa
        </button>
      </div>

      {aba === 'LISTA' ? (
        <div className="stack">
          <p className="callout">
            A lista técnica: cada linha é uma faixa de horário com um grau. Use para o que não é professor — uma turma
            que só pode de manhã, uma sala fechada na sexta — e para as preferências mais finas, como "prefere não dar
            aula na sexta". Para a disponibilidade de um professor, a semana é mais fácil.
          </p>
          {listaCompleta}
        </div>
      ) : (
        <div className="disp">
          <aside className="disp__lista" aria-label="Professores" data-tour="professores">
            <input
              className="input"
              type="search"
              placeholder={`Procurar entre ${lista.length} professores…`}
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              aria-label="Procurar professor por nome ou matrícula"
            />
            <div className="disp__filtros" role="group" aria-label="Filtrar">
              {(
                [
                  ['TODOS', 'Todos'],
                  ['NAO_COBRE', 'Não cobrem a carga'],
                  ['SEM_CARGA', 'Sem carga'],
                ] as const
              ).map(([id, rotulo]) => (
                <button
                  key={id}
                  type="button"
                  className="disp__filtro"
                  aria-pressed={filtro === id}
                  onClick={() => setFiltro(id)}
                >
                  {rotulo} <span className="mono">{quantos(id)}</span>
                </button>
              ))}
            </div>
            <ul className="disp__nomes">
              {visiveis.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    className="disp__nome"
                    aria-current={escolhido === p.id ? 'true' : undefined}
                    onClick={() => escolher(p.id)}
                  >
                    <span>{rotulos.get(p.id) ?? p.nome}</span>
                    {situacao(p)}
                  </button>
                </li>
              ))}
              {visiveis.length === 0 && <li className="field__hint">Ninguém com esse nome.</li>}
            </ul>
          </aside>

          <section className="disp__painel" aria-live="polite">
            {!escolhido ? (
              <div className="empty">
                <h2>Escolha um professor</h2>
                <p>
                  A semana aparece aqui, em quadradinhos: <strong>pode</strong> é horário em que pode dar aula;{' '}
                  <strong>não pode</strong> é horário em que o sistema não põe aula.
                </p>
                <p className="field__hint">
                  Dica: comece por "Não cobrem a carga" — é quem liberou menos horário do que as aulas
                  atribuídas precisam, e é onde a grade vai travar.
                </p>
              </div>
            ) : !semana ? (
              <p className="field__hint">Carregando…</p>
            ) : (
              <div className="stack">
                <div className="disp__topo">
                  <div>
                    <h2>{rotulos.get(semana.professor.id) ?? semana.professor.nome}</h2>
                    {semana.professor.codigo_externo && (
                      <p className="field__hint">matrícula {semana.professor.codigo_externo}</p>
                    )}
                  </div>
                  {editando && (
                    <div className="row">
                      <button type="button" className="btn btn--ghost" onClick={() => mudar(todos, true)}>
                        Pode em tudo
                      </button>
                      <button type="button" className="btn btn--ghost" onClick={() => mudar(todos, false)}>
                        Não pode em nada
                      </button>
                    </div>
                  )}
                </div>

                {semana.pedido ? (
                  <div className="disp__pedido" data-tour="pedido">
                    <p className="field__label">O que a carga pede</p>
                    <ul>
                      {semana.frases.map((f, i) => {
                        const e = semana.pedido!.exigencias[i]
                        const ok = e ? n[i]! >= e.aulas : true
                        return (
                          <li key={f} className={e ? (ok ? 'formulario__ok' : 'formulario__falta') : undefined}>
                            {e && <span aria-hidden>{ok ? '✓ ' : '! '}</span>}
                            {f}
                            {e && (
                              <span className="mono">
                                {' '}
                                — {ok ? 'coberto' : `pode em ${n[i]}, precisa de ${e.aulas}`}
                              </span>
                            )}
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                ) : (
                  <p className="callout">
                    Sem aula atribuída por enquanto. Esta disponibilidade vale para quando houver alguma.
                  </p>
                )}

                {editando ? (
                  <div className="disp__negociacao disp__negociacao--editando" role="status">
                    <p>
                      <strong>Editando a disponibilidade, como combinado com o professor:</strong> “{combinado}”
                    </p>
                    <p className="field__hint">
                      Ao salvar, o combinado e o que mudou entram no histórico da negociação, com o seu nome.
                    </p>
                  </div>
                ) : negociacao ? (
                  <div className="disp__negociacao" data-tour="negociacao">
                    <p>
                      <strong>Negociação {ESTADO_DA_NEGOCIACAO[negociacao.estado]}</strong>
                      {negociacao.responsavel && <> · com {negociacao.responsavel}</>}
                    </p>
                    {negociacao.ultima_nota && <p className="field__hint">Última nota: “{negociacao.ultima_nota}”</p>}
                    {!confirmando ? (
                      <div className="row">
                        <button type="button" className="btn btn--secondary" onClick={() => setConfirmando(true)}>
                          Alterar disponibilidade…
                        </button>
                        <a className="field__hint" href="?tela=pendencias">
                          Ver em Pendências
                        </a>
                      </div>
                    ) : (
                      <div className="disp__confirmar stack">
                        <p>
                          <strong>A alteração manual muda a disponibilidade em nome do professor.</strong> Ela vale como
                          se ele tivesse respondido, e o motor passa a usá-la na próxima grade. Só altere o que foi
                          combinado com ele.
                        </p>
                        <label className="checkbox">
                          <input type="checkbox" checked={acordo} onChange={(e) => setAcordo(e.target.checked)} />
                          Esta mudança foi combinada com o professor
                        </label>
                        <label className="field">
                          <span className="field__label">O que foi combinado</span>
                          <input
                            className="input"
                            value={combinado}
                            onChange={(e) => setCombinado(e.target.value)}
                            placeholder="por telefone em 04/11: libera quinta à noite, não pode mais segunda de manhã"
                          />
                        </label>
                        <div className="row">
                          <button
                            type="button"
                            className="btn btn--primary"
                            disabled={!acordo || !combinado.trim()}
                            onClick={() => {
                              setEditando(true)
                              setConfirmando(false)
                            }}
                          >
                            Liberar edição
                          </button>
                          <button type="button" className="btn btn--ghost" onClick={cancelarEdicao}>
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="disp__negociacao disp__negociacao--abrir" data-tour="negociacao">
                    <p>
                      {semana.pedido && !salvoCobre ? (
                        <>
                          <strong>O que foi liberado não cobre as aulas atribuídas.</strong>{' '}
                        </>
                      ) : null}
                      Esta é a disponibilidade que o professor informou, e só ele a muda — pelo{' '}
                      <a href="?tela=formularios">formulário por link</a>. Se precisar alterar por ele, comece uma
                      negociação: ela vai para Pendências, com você como responsável, e libera a edição aqui.
                    </p>
                    <label className="field">
                      <span className="field__label">Nota (opcional)</span>
                      <input
                        className="input"
                        value={nota}
                        onChange={(e) => setNota(e.target.value)}
                        placeholder={
                          semana.pedido && !salvoCobre
                            ? 'liguei, vai ver se consegue a quinta'
                            : 'pediu para trocar a terça pela quinta'
                        }
                      />
                    </label>
                    <div className="row">
                      <button
                        type="button"
                        className="btn btn--secondary"
                        disabled={negociando}
                        onClick={() => void iniciarNegociacao()}
                      >
                        {negociando ? 'Iniciando…' : 'Iniciar negociação'}
                      </button>
                    </div>
                  </div>
                )}

                <LegendaDaSemana />
                <div data-tour="semana">
                  <SemanaDeDisponibilidade
                    turnos={semana.turnos}
                    tempos={semana.tempos}
                    marcados={marcados}
                    onMudar={mudar}
                    somenteLeitura={!editando}
                  />
                </div>
                {editando && (
                  <p className="field__hint">
                    Clique num quadradinho para trocar. Clique no nome do dia para trocar a coluna inteira daquele turno,
                    e no horário para trocar a linha.
                  </p>
                )}
                {semana.outras_preferencias > 0 && (
                  <p className="field__hint">
                    Há também {semana.outras_preferencias} preferência(s) mais fina(s) ("prefere", "prefere não"),
                    que não mudam aqui — estão na Lista completa.
                  </p>
                )}

                {editando ? (
                  <div className="row disp__rodape">
                    <button
                      type="button"
                      className="btn btn--primary"
                      disabled={!alterado || salvando}
                      onClick={() => void salvar()}
                      data-tour="salvar"
                    >
                      {salvando ? 'Salvando…' : 'Salvar disponibilidade'}
                    </button>
                    <button type="button" className="btn btn--ghost" disabled={salvando} onClick={cancelarEdicao}>
                      Cancelar
                    </button>
                    {alterado && <span className="disp__pendente">mudanças não salvas</span>}
                  </div>
                ) : null}
                {aviso && (
                  <p className={`state ${aviso.tipo === 'ok' ? 'state--ok' : 'state--pendencia'}`} role="status">
                    {aviso.texto}
                  </p>
                )}
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  )
}
